import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { SubmittedCard } from '../components/cards/SubmittedCard';
import { Scoreboard } from '../components/Scoreboard';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { useGame } from '../state/gameContext';
import { playerToScoreboard } from '../state/mockGameReducer';
import { playSound } from '../state/soundManager';

export default function JudgeRevealPage() {
  const { state, currentPlayer, pickWinner, pickWinnerByIndex } = useGame();
  const [revealedRound, setRevealedRound] = useState<number | null>(null);
  const [selection, setSelection] = useState<{ round: number; id: string } | null>(null);
  const revealed = revealedRound === state.roundNumber;
  const selectedSubmissionId = selection?.round === state.roundNumber ? selection.id : null;
  if (!state.roomCode || state.submissions.length === 0) return <Navigate to="/game" replace />;
  const judgePlayer = state.players.find((p) => p.id === state.judgeId);
  const isJudge = currentPlayer?.id === state.judgeId;
  const canPick = state.canPickWinner ?? isJudge;
  const handlePickWinner = () => {
    if (!canPick || !revealed) return;
    const sub = state.submissions.find((s) => s.id === selectedSubmissionId);
    if (!sub) return;
    if (sub.revealIndex !== undefined) pickWinnerByIndex(sub.revealIndex);
    else pickWinner(sub.playerId);
  };
  return <div className="round-screen">
    <TopNav showChat />
    <Scoreboard players={state.players.map((p) => playerToScoreboard(p, state.judgeId))} />
    <main className="judge-workspace" aria-label="Judge the answers">
      <header className="judge-heading">
        <div><p className="table-eyebrow">Round {state.roundNumber} · {state.submissions.length} {state.submissions.length === 1 ? 'answer' : 'answers'}</p>
          <h1>{isJudge ? 'You are the judge.' : `${judgePlayer?.name ?? 'The judge'} is choosing.`}</h1></div>
        <span className="text-xs text-on-surface-variant">Room {state.roomCode}</span>
      </header>
      <section className="judge-prompt" aria-label="Round prompt" tabIndex={0} data-testid="judge-prompt">
        <span className="table-eyebrow">The prompt · Pick {state.blackCardPick}</span>
        <p>{state.blackCardText}</p>
      </section>
      <section className="judge-answers" aria-label="Submitted answers" tabIndex={0}>
        <div className="judge-answer-grid">
          {state.submissions.map((sub, index) => <SubmittedCard key={sub.id} testId="submission-card" text={sub.text}
            revealed={revealed} selected={selectedSubmissionId === sub.id} number={index + 1}
            onClick={revealed && canPick ? () => setSelection({ round: state.roundNumber, id: sub.id }) : undefined} />)}
        </div>
      </section>
      <footer className="judge-actions">
        <p role="status">{!revealed ? 'Everyone is in. Ready for the answers?' : canPick ? selectedSubmissionId ? 'Your pick is marked. Confirm when ready.' : 'Choose your favorite answer, then pick the winner.' : 'Read the answers while the judge decides.'}</p>
        {!revealed ? <button type="button" data-testid="reveal-all" className="table-button" onClick={() => { playSound('reveal'); setRevealedRound(state.roundNumber); }}>Reveal All</button>
          : canPick ? <button type="button" data-testid="pick-winner" className="table-button disabled:opacity-40" disabled={!selectedSubmissionId} onClick={handlePickWinner}>Pick Winner</button>
          : <span className="text-primary text-sm">Waiting for the judge</span>}
      </footer>
    </main>
    <MobileBottomNav active="/game" />
  </div>;
}
